import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  HUBSPOT_PULL_FIELD_LABELS,
  type HubSpotFieldConflict,
  type HubSpotPullField,
} from "../utils/hubspot";

/**
 * Per-field reconciliation for a HubSpot pull whose values differ from
 * what the form already holds (ADR-172 review-first model). Purely
 * client-side: each row shows the form's value vs the CRM's with a
 * keep-ours / take-HubSpot toggle. Toggles default to "Take HubSpot" —
 * the organizer opened this flow to pull from the CRM, so pulling is
 * the expected choice and keeping ours is the per-field opt-out.
 * Confirm hands the chosen fields back to the page, which updates the
 * FORM — nothing persists until the organizer saves the profile.
 */

export interface HubSpotReconcileTarget {
  displayName: string;
  conflicts: HubSpotFieldConflict[];
}

interface HubSpotReconcileDialogProps {
  /** null = closed. */
  target: HubSpotReconcileTarget | null;
  onClose: () => void;
  /** Fields the organizer chose "take HubSpot" for (may be empty). */
  onApply: (takeFields: HubSpotPullField[]) => void;
}

export function HubSpotReconcileDialog({
  target,
  onClose,
  onApply,
}: HubSpotReconcileDialogProps) {
  const [takeFields, setTakeFields] = useState<Set<HubSpotPullField>>(
    new Set(),
  );

  // Reset choices whenever the dialog opens for a new pull — every
  // conflict starts on "Take HubSpot".
  useEffect(() => {
    setTakeFields(new Set(target?.conflicts.map((c) => c.field) ?? []));
  }, [target]);

  function handleConfirm() {
    onApply([...takeFields]);
    onClose();
  }

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reconcile with HubSpot</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          These fields differ between the profile form and HubSpot. The
          HubSpot value is preselected for each — switch any you&#39;d
          rather keep to &ldquo;Keep ours&rdquo;. Nothing is saved until
          you save the profile.
        </DialogContentText>
        <Stack spacing={1.5}>
          {(target?.conflicts ?? []).map((conflict) => {
            const taking = takeFields.has(conflict.field);
            return (
              <Box
                key={conflict.field}
                sx={{ pl: 1.5, borderLeft: 2, borderColor: "warning.light" }}
                data-testid={`hubspot-conflict-${conflict.field}`}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 500, display: "block" }}
                >
                  {HUBSPOT_PULL_FIELD_LABELS[conflict.field]}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <span
                    style={
                      taking ? { textDecoration: "line-through" } : undefined
                    }
                  >
                    {conflict.ours}
                  </span>{" "}
                  →{" "}
                  <strong
                    style={
                      taking ? undefined : { textDecoration: "line-through" }
                    }
                  >
                    {conflict.theirs}
                  </strong>
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={taking ? "theirs" : "ours"}
                  onChange={(_, value) => {
                    if (value === null) return;
                    setTakeFields((prev) => {
                      const next = new Set(prev);
                      if (value === "theirs") next.add(conflict.field);
                      else next.delete(conflict.field);
                      return next;
                    });
                  }}
                  aria-label={`Choose value for ${HUBSPOT_PULL_FIELD_LABELS[conflict.field]}`}
                >
                  <ToggleButton value="ours" sx={{ textTransform: "none" }}>
                    Keep ours
                  </ToggleButton>
                  <ToggleButton value="theirs" sx={{ textTransform: "none" }}>
                    Take HubSpot
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          data-testid="hubspot-reconcile-confirm"
        >
          Link &amp; review
        </Button>
      </DialogActions>
    </Dialog>
  );
}
